import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM, fmtRMSigned } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Loader2, ArrowUpCircle, ArrowDownCircle, Banknote, Trash2, Search } from "lucide-react";

const TYPE_META = {
  "Capital In": { cls: "bg-green-50 text-green-700 border-green-200", icon: ArrowUpCircle, sign: "+" },
  "Collection In": { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: ArrowUpCircle, sign: "+" },
  Expense: { cls: "bg-red-50 text-red-700 border-red-200", icon: ArrowDownCircle, sign: "−" },
  "New Loan Out": { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Banknote, sign: "−" },
};

// Auto descriptions look like "Payment from {name} ({code})" / "Disbursement to {name} ({code})".
// Surface the customer name and loan code as clickable links.
function LedgerDescription({ description }) {
  const desc = description || "—";
  const m = desc.match(/^(Disbursement to|Payment from)\s+(.+?)\s+\(([^)]+)\)$/);
  if (!m) return <>{desc}</>;
  const [, verb, name, code] = m;
  return (
    <>
      {verb}{" "}
      <Link
        data-testid={`ledger-customer-link-${code}`}
        to={`/customer?q=${encodeURIComponent(name)}`}
        className="text-slate-900 font-medium hover:underline"
      >
        {name}
      </Link>{" "}
      (
      <Link
        data-testid={`ledger-loan-link-${code}`}
        to={`/loans/${code}`}
        className="font-mono-fig text-slate-900 hover:underline"
      >
        {code}
      </Link>
      )
    </>
  );
}

export default function BranchLedger() {
  const { t } = useLang();
  const [data, setData] = useState({ entries: [], currentBalance: 0, months: [], month: "" });
  const [month, setMonth] = useState("");
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), type: "Capital In", description: "", amount: "" });
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = (m) => {
    setLoading(true);
    return api.get("/branch-ledger", { params: m ? { month: m } : {} })
      .then((r) => { setData(r.data); setMonth(r.data.month); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const monthLabel = (m) => {
    if (!m) return "";
    const [y, mo] = m.split("-");
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-MY", { month: "long", year: "numeric" });
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount) { toast.error(t("ledger.toast_amount")); return; }
    setSaving(true);
    try {
      await api.post("/branch-ledger", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success(t("ledger.toast_added"));
      setForm({ ...form, description: "", amount: "" });
      load(month);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/branch-ledger/${id}`);
    toast.success(t("ledger.toast_removed"));
    load(month);
  };

  const q = query.trim().toLowerCase();
  const filteredEntries = q
    ? data.entries.filter((e) =>
        `${e.description || ""} ${e.type || ""} ${e.date || ""}`.toLowerCase().includes(q)
      )
    : data.entries;

  return (
    <Layout>
      <PageHeader
        title={t("ledger.title")}
        subtitle={t("ledger.subtitle")}
        action={
          <div className="bg-slate-900 text-white rounded-lg px-5 py-2.5">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">{t("ledger.current_balance")}</div>
            <div data-testid="ledger-balance" className="font-mono-fig text-xl font-bold">{fmtRMSigned(data.currentBalance)}</div>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 h-fit">
          <h3 className="font-heading font-semibold text-slate-900 mb-4">{t("ledger.new_entry")}</h3>
          <form onSubmit={submit} className="space-y-4" data-testid="ledger-form">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">{t("col.type")}</label>
              <select data-testid="ledger-type-select" className={`${inputCls} mt-1.5`} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="Capital In">{t("type.Capital In")}</option>
                <option value="Collection In">{t("type.Collection In")}</option>
                <option value="Expense">{t("type.Expense")}</option>
                <option value="New Loan Out">{t("type.New Loan Out")}</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">{t("col.date")}</label>
              <input type="date" data-testid="ledger-date-input" className={`${inputCls} mt-1.5`} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">{t("col.description")}</label>
              <input data-testid="ledger-desc-input" className={`${inputCls} mt-1.5`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("ledger.desc_ph")} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">{t("ledger.amount_rm")}</label>
              <input type="number" step="0.01" data-testid="ledger-amount-input" className={`${inputCls} mt-1.5`} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
            </div>
            <button data-testid="ledger-submit-btn" type="submit" disabled={saving} className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("ledger.add_entry")}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-heading font-semibold text-slate-900">{t("ledger.timeline")}</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid="ledger-month-select"
                value={month}
                onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
                className="py-1.5 pl-3 pr-8 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 focus:ring-2 focus:ring-slate-900 outline-none cursor-pointer"
              >
                {(data.months || []).map((m) => (
                  <option key={m} value={m}>{monthLabel(m)}</option>
                ))}
              </select>
              <div className="relative w-full sm:w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="ledger-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("ledger.search_month")}
                  className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
                />
              </div>
            </div>
          </div>
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5 font-medium">{t("col.date")}</th>
                <th className="px-3 py-2.5 font-medium">{t("col.type")}</th>
                <th className="px-3 py-2.5 font-medium">{t("col.description")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("col.amount")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("col.balance")}</th>
                <th className="px-5 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((e) => {
                const meta = TYPE_META[e.type] || {};
                const Icon = meta.icon || Banknote;
                const isIn = e.type === "Capital In" || e.type === "Collection In";
                const delta = isIn ? e.amount : -e.amount;
                const pos = delta >= 0;
                return (
                  <tr key={e.entryId} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{e.date}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border ${meta.cls}`}>
                        <Icon className="h-3 w-3" /> {t(`type.${e.type}`)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-600"><LedgerDescription description={e.description} />{e.auto && <span className="ml-1 text-[10px] text-slate-400">{t("ledger.auto")}</span>}</td>
                    <td className={`px-3 py-3 text-right font-mono-fig ${pos ? "text-green-600" : "text-red-600"}`}>{pos ? "+" : "−"}{fmtRM(Math.abs(delta))}</td>
                    <td className="px-3 py-3 text-right font-mono-fig font-semibold text-slate-900">{fmtRMSigned(e.runningBalance)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => remove(e.entryId)} className="text-slate-300 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
              {!loading && filteredEntries.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400">{data.entries.length === 0 ? t("ledger.no_entries") : t("ledger.no_match")}</td></tr>}
              {loading && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400 animate-pulse">{t("common.loading")}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
